import { Link } from "@ui/Atoms/Link/Link";
import type { PaginatorTranslations } from "./types";

type Props = {
	currentPage: number;
	totalPages: number;
	hasPreviousPage: boolean;
	hasNextPage: boolean;
	pageHref: (page: number) => string;
	translations: PaginatorTranslations;
};

function pageNumbers(current: number, total: number): (number | "…")[] {
	if (total <= 7) {
		return Array.from({ length: total }, (_, i) => i + 1);
	}

	const pages: (number | "…")[] = [1];
	const rangeStart = Math.max(2, current - 1);
	const rangeEnd = Math.min(total - 1, current + 1);

	if (rangeStart > 2) pages.push("…");
	for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
	if (rangeEnd < total - 1) pages.push("…");

	pages.push(total);
	return pages;
}

const btnBase =
	"inline-flex items-center justify-center min-w-9 h-9 px-2.5 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-default)] text-[13px] font-medium text-[var(--content-primary)] no-underline cursor-pointer font-[inherit] transition-[background,border-color] duration-[120ms] whitespace-nowrap sm:min-w-9 sm:h-9 max-sm:flex-1 max-sm:max-w-[120px] max-sm:h-11 max-sm:text-[14px]";
const btnHover =
	"hover:bg-[var(--surface-elevated)] hover:border-[var(--border-default)]";
const btnActive =
	"bg-[var(--content-primary)] border-[var(--content-primary)] text-[var(--content-inverted)] cursor-default pointer-events-none";
const btnDisabled = "opacity-35 cursor-default pointer-events-none";

export function Paginator({
	currentPage,
	totalPages,
	hasPreviousPage,
	hasNextPage,
	pageHref,
	translations,
}: Props) {
	if (totalPages <= 1) return null;

	const items = pageNumbers(currentPage, totalPages);

	return (
		<nav
			className="flex items-center justify-center gap-1 pt-6 pb-2 flex-wrap max-sm:flex-nowrap max-sm:justify-between max-sm:gap-0"
			aria-label={translations.navAriaLabel}
		>
			<Link
				href={hasPreviousPage ? pageHref(currentPage - 1) : undefined}
				className={`${btnBase} ${hasPreviousPage ? btnHover : btnDisabled}`}
				aria-label={translations.previousAriaLabel}
				aria-disabled={!hasPreviousPage}
			>
				&#8249; {translations.previousLabel}
			</Link>

			<span className="contents max-sm:hidden">
				{items.map((item, i) =>
					item === "…" ? (
						<span
							// biome-ignore lint/suspicious/noArrayIndexKey: ellipsis has no identity of its own, position disambiguates repeats
							key={`ellipsis-${i}`}
							className="inline-flex items-center justify-center min-w-9 h-9 text-[13px] text-[var(--content-muted)] select-none"
						>
							…
						</span>
					) : (
						<Link
							key={item}
							href={item === currentPage ? undefined : pageHref(item)}
							className={`${btnBase} ${item === currentPage ? btnActive : btnHover}`}
							aria-label={translations.pageAriaLabel(item)}
							aria-current={item === currentPage ? "page" : undefined}
						>
							{item}
						</Link>
					),
				)}
			</span>

			<span
				className="hidden max-sm:flex items-center justify-center flex-1 text-[13px] font-medium text-[var(--content-secondary)] whitespace-nowrap"
				aria-hidden="true"
			>
				{translations.counterLabel(currentPage, totalPages)}
			</span>

			<Link
				href={hasNextPage ? pageHref(currentPage + 1) : undefined}
				className={`${btnBase} ${hasNextPage ? btnHover : btnDisabled}`}
				aria-label={translations.nextAriaLabel}
				aria-disabled={!hasNextPage}
			>
				{translations.nextLabel} &#8250;
			</Link>
		</nav>
	);
}
